import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  deleteField,
} from 'firebase/firestore';
import { db } from './config';
import { CharacterData, normalizeCharacterData } from '../types/character';

const COLLECTIONS = {
  USERS: 'users',
  CHARACTERS: 'characters',
} as const;

export const getUserCharactersRef = (userId: string) => {
  return collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.CHARACTERS);
};

export const getCharacterRef = (userId: string, characterId: string) => {
  return doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.CHARACTERS, characterId);
};

export const createCharacter = async (
  userId: string,
  characterData: Omit<CharacterData, 'id'>
): Promise<string> => {
  const charactersRef = getUserCharactersRef(userId);
  const characterDoc = doc(charactersRef);
  
  const dataWithTimestamp = {
    ...characterData,
    lastModified: serverTimestamp(),
  };
  
  await setDoc(characterDoc, dataWithTimestamp);
  return characterDoc.id;
};

export const updateCharacter = async (
  userId: string,
  characterId: string,
  updates: Partial<CharacterData>
): Promise<void> => {
  const characterRef = getCharacterRef(userId, characterId);
  
  const updatesWithTimestamp = {
    ...updates,
    lastModified: serverTimestamp(),
  };
  
  await updateDoc(characterRef, updatesWithTimestamp);
};

export const getCharacter = async (
  userId: string,
  characterId: string
): Promise<CharacterData | null> => {
  const characterRef = getCharacterRef(userId, characterId);
  const docSnap = await getDoc(characterRef);
  
  if (docSnap.exists()) {
    const rawData = { id: docSnap.id, ...docSnap.data() };
    return normalizeCharacterData(rawData);
  }
  
  return null;
};

export const getUserCharacters = async (userId: string): Promise<CharacterData[]> => {
  const charactersRef = getUserCharactersRef(userId);
  const querySnapshot = await getDocs(charactersRef);
  
  return querySnapshot.docs.map(doc => {
    const rawData = { id: doc.id, ...doc.data() };
    return normalizeCharacterData(rawData);
  });
};

export const deleteCharacter = async (
  userId: string,
  characterId: string
): Promise<void> => {
  const characterRef = getCharacterRef(userId, characterId);
  await deleteDoc(characterRef);
};

export const subscribeToCharacter = (
  userId: string,
  characterId: string,
  callback: (character: CharacterData | null) => void
): Unsubscribe => {
  const characterRef = getCharacterRef(userId, characterId);
  
  return onSnapshot(characterRef, (doc) => {
    if (doc.exists()) {
      const rawData = { id: doc.id, ...doc.data() };
      callback(normalizeCharacterData(rawData));
    } else {
      callback(null);
    }
  });
};

export const subscribeToUserCharacters = (
  userId: string,
  callback: (characters: CharacterData[]) => void
): Unsubscribe => {
  const charactersRef = getUserCharactersRef(userId);
  
  return onSnapshot(charactersRef, (querySnapshot) => {
    const characters = querySnapshot.docs.map(doc => {
      const rawData = { id: doc.id, ...doc.data() };
      return normalizeCharacterData(rawData);
    });

    callback(characters);
  });
};

// Export Firebase utilities for use in other modules
export { deleteField };